import {createAsyncThunk, createSlice, PayloadAction} from "@reduxjs/toolkit";
import {Menu} from "@sobr/wine-menu-client";
import {menuApi} from "lib/api/wineMenuApi";
import {AppDispatch, AppState} from "lib/redux/store";
import {
    createRequestFunction,
    DUMMY_BASE_URL,
    serializeDataIfNeeded,
    toPathString
} from "@sobr/wine-menu-client/dist/common";

interface UserMenuData {
    invalidated: boolean
    menus: Menu[]
    request: {
        loading: boolean
        error: string | null
    }
}

interface MenuManagementState {
    renameModal: {
        open: boolean,
        menuId: string | null,
        menuName: string | null,
        nameInputValue: string,
        pending: boolean,
        error: string | null
    },
    deleteModal: {
        open: boolean,
        menuId: string | null,
        menuName: string | null,
        pending: boolean,
        error: string | null
    },
    copyModal: {
        open: boolean,
        menuId: string | null,
        menuName: string | null,
        copyToOtherCustomer: boolean,
        copyToUserId: string,
        pending: boolean,
        error: string | null
    },
    copyNotification: {
        open: boolean,
        menuName: string | null,
        userId: string | null
    },
    data: {
        [userId: string]: UserMenuData
    }
}

const initialState: MenuManagementState = {
    renameModal: {
        open: false,
        menuId: null,
        menuName: null,
        nameInputValue: "",
        pending: false,
        error: null
    },
    deleteModal: {
        open: false,
        menuId: null,
        menuName: null,
        pending: false,
        error: null
    },
    copyModal: {
        open: false,
        menuId: null,
        menuName: null,
        copyToOtherCustomer: false,
        copyToUserId: "",
        pending: false,
        error: null
    },
    copyNotification: {
        open: false,
        menuName: null,
        userId: null
    },
    data: {}
};

const fetchMenus = createAsyncThunk(
    'menuManagement/fetch',
    async (userId: string) => {
        const response = await menuApi.getMenusForUser({userId: userId});
        return response.data;
    }
);

interface RenameRequest {
    userId: string,
    fileId: string,
    name: string
}

const renameMenu = createAsyncThunk<Menu,
    RenameRequest,
    {
        dispatch: AppDispatch
    }>(
    'menuManagement/rename',
    async (request: RenameRequest, thunkApi) => {
        const response = await menuApi.replaceMenu({
            userId: request.userId,
            fileId: request.fileId,
            name: request.name
        });

        thunkApi.dispatch(actions.closeRenameModal());
        thunkApi.dispatch(fetchMenus(request.userId));

        return response.data;
    }
);

interface DeleteRequest {
    userId: string,
    fileId: string,
}

const deleteMenu = createAsyncThunk<void, DeleteRequest, {dispatch: AppDispatch}>(
    'menuManagement/delete',
    async (request: DeleteRequest, thunkApi) => {

        const response = await menuApi.deleteMenu(request);

        thunkApi.dispatch(actions.closeDeleteModal());
        thunkApi.dispatch(fetchMenus(request.userId));

        return response.data;
    }
);

interface CopyRequest {
    userId: string;
    fileId: string;
    menuName: string;
    copyToUserId: string;
}


const copyMenuApiCall = async (fileId: string, fromUserId: string, toUserId: string) => {
    const configuration = (menuApi as unknown as any).configuration;

    const accessToken = JSON.parse(localStorage.getItem("auth") || "{}").accessToken;

    const localVarPath = `/copyMenu`;
    const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
    let baseOptions;
    if (configuration) {
        baseOptions = configuration.baseOptions;
    }
    const localVarRequestOptions = { method: 'POST', ...baseOptions};
    const localVarHeaderParameter = {} as any;
    localVarHeaderParameter['Content-Type'] = 'application/json';
    localVarHeaderParameter['Authorization'] = "Bearer " + accessToken;
    let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
    localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions};
    localVarRequestOptions.data = serializeDataIfNeeded(
        {fileId, fromUserId, toUserId},
        localVarRequestOptions,
        configuration
    );
    const urlParams = {
        url: toPathString(localVarUrlObj),
        options: localVarRequestOptions,
    };

    const requestFunction = await createRequestFunction(
        urlParams,
        (menuApi as unknown as any).axios,
        (menuApi as unknown as any).basePath
    );

    return requestFunction((menuApi as unknown as any).axios, (menuApi as unknown as any).basePath);
};


const copyMenu = createAsyncThunk<void, CopyRequest, {dispatch: AppDispatch}>(
    'menuManagement/copy',
    async (request: CopyRequest, thunkApi) => {
        await copyMenuApiCall(request.fileId, request.userId, request.copyToUserId);
        thunkApi.dispatch(actions.showCopyNotification({
            menuName: request.menuName,
            userId: request.copyToUserId
        }));
        setTimeout(
            ()=>thunkApi.dispatch(actions.hideCopyNotification()),
            5000
        );
        thunkApi.dispatch(actions.closeCopyModal());
        thunkApi.dispatch(fetchMenus(request.userId));
    }
);

function fetchMenusIfNecessary(userId: string) {
    return (dispatch: AppDispatch, getState: () => AppState) => {
        const state = getState();
        const isInvalid = state.menu.management.data[userId]?.invalidated ?? true;
        if (isInvalid) {
            return dispatch(fetchMenus(userId));
        } else {
            return Promise.resolve([] as Menu[]);
        }
    }
}

const {actions, reducer} = createSlice({
    name: 'menuManagement',
    initialState: initialState,
    reducers: {
        openRenameModal: (state, action: PayloadAction<Menu>) => {
            state.renameModal.open = true;
            state.renameModal.menuId = action.payload.id ?? null;
            state.renameModal.menuName = action.payload.name ?? "";
        },
        closeRenameModal: (state) => {
            state.renameModal.open = false;
            state.renameModal.menuId = null;
            state.renameModal.menuName = null;
            state.renameModal.nameInputValue = "";
        },
        setRenameInputValue: (state, action: PayloadAction<string>) => {
            state.renameModal.nameInputValue = action.payload;
        },
        setRenameError: (state, action: PayloadAction<string | null>) => {
            state.renameModal.error = action.payload;
        },

        openDeleteModal: (state, action: PayloadAction<Menu>) => {
            state.deleteModal.open = true;
            state.deleteModal.menuId = action.payload.id ?? null;
            state.deleteModal.menuName = action.payload.name ?? null;
        },
        closeDeleteModal: (state) => {
            state.deleteModal.open = false;
            state.deleteModal.menuId = null;
        },
        setDeleteError: (state, action: PayloadAction<string | null>) => {
            state.deleteModal.error = action.payload;
        },

        openCopyModal: (state, action: PayloadAction<Menu>) => {
            state.copyModal.open = true;
            state.copyModal.menuId = action.payload.id ?? null;
            state.copyModal.menuName = action.payload.name ?? null;
        },
        setCopyToUserId: (state, action: PayloadAction<string>) => {
            state.copyModal.copyToUserId = action.payload;
        },
        setCopyToOtherCustomer: (state, action: PayloadAction<boolean>) => {
            state.copyModal.copyToOtherCustomer = action.payload;
        },
        closeCopyModal: (state) => {
            state.copyModal.open = false;
            state.copyModal.menuId = null;
            state.copyModal.copyToUserId = "";
            state.copyModal.copyToOtherCustomer = false;
            state.copyModal.error = null;
        },
        setCopyError: (state, action: PayloadAction<string | null>) => {
            state.copyModal.error = action.payload;
        },
        showCopyNotification: (state, action: PayloadAction<{menuName: string, userId: string}>) => {
            const {menuName, userId} = action.payload;
            state.copyNotification = {
                open: true,
                menuName,
                userId
            }
        },
        hideCopyNotification: state => {
            state.copyNotification = {open: false, menuName: null, userId: null};
        },

        invalidateData: (state, action: PayloadAction<string>) => {
            if (action.payload in state.data) {
                state.data[action.payload].invalidated = true;
            }
        }
    },
    extraReducers: builder => {
        builder.addCase(fetchMenus.pending, (state, action) => {
            state.data[action.meta.arg] = {
                invalidated: false,
                menus: [],
                request: {
                    loading: true,
                    error: null
                }
            };
        });

        builder.addCase(fetchMenus.fulfilled, (state, action) => {
            state.data[action.meta.arg].request.loading = false;
            state.data[action.meta.arg].request.error = null;
            state.data[action.meta.arg].invalidated = false;
            state.data[action.meta.arg].menus = action.payload;
        });

        builder.addCase(fetchMenus.rejected, (state, action) => {
            state.data[action.meta.arg].request.loading = false;
            state.data[action.meta.arg].request.error = action.error.message?.toString() ?? "unknown error";
            state.data[action.meta.arg].invalidated = true;
            state.data[action.meta.arg].menus = [];
        });


        builder.addCase(renameMenu.pending, (state, action) => {
            state.renameModal.pending = true;
            state.renameModal.error = null;
        });

        builder.addCase(renameMenu.fulfilled, (state, action) => {
            state.renameModal.pending = false;
            state.renameModal.error = null;
            state.renameModal.nameInputValue = "";
        });

        builder.addCase(renameMenu.rejected, (state, action) => {
            state.renameModal.pending = false;
            state.renameModal.error = action.error.message ?? null;
        });


        builder.addCase(deleteMenu.pending, (state, action) => {
            state.deleteModal.pending = true;
            state.deleteModal.error = null;
        });

        builder.addCase(deleteMenu.fulfilled, (state, action) => {
            state.deleteModal.pending = false;
            state.deleteModal.error = null;
        });

        builder.addCase(deleteMenu.rejected, (state, action) => {
            state.deleteModal.pending = false;
            state.deleteModal.error = action.error.message ?? null;
        });


        builder.addCase(copyMenu.pending, (state, action) => {
            state.copyModal.pending = true;
            state.copyModal.error = null;
        });

        builder.addCase(copyMenu.fulfilled, (state, action) => {
            state.copyModal.pending = false;
            state.copyModal.error = null;
            state.copyModal.copyToUserId = "";
            state.copyModal.copyToOtherCustomer = false;
        });

        builder.addCase(copyMenu.rejected, (state, action) => {
            state.copyModal.pending = false;
            state.copyModal.error = action.error.message ?? null;
        });
    }
});

const publicActions = {
    openRenameModal: actions.openRenameModal,
    closeRenameModal: actions.closeRenameModal,
    setRenameInputValue: actions.setRenameInputValue,
    setRenameError: actions.setRenameError,
    renameMenu,

    openDeleteModal: actions.openDeleteModal,
    closeDeleteModal: actions.closeDeleteModal,
    setDeleteError: actions.setDeleteError,
    deleteMenu,

    openCopyModal: actions.openCopyModal,
    closeCopyModal: actions.closeCopyModal,
    setCopyError: actions.setCopyError,
    setCopyToOtherCustomer: actions.setCopyToOtherCustomer,
    setCopyToUserId: actions.setCopyToUserId,
    copyMenu,

    fetchMenusIfNecessary,
    invalidateData: actions.invalidateData,
}

function getMenuManagementState(state: AppState): MenuManagementState {
    return state.menu.management;
}

function getData(state: AppState, userId: string | null | undefined): UserMenuData | null {
    return userId != null ?
        getMenuManagementState(state).data[userId] :
        null;
}

const selectors = {
    renameModalOpen: (state: AppState) => getMenuManagementState(state).renameModal.open,
    renameModalMenuId: (state: AppState) => getMenuManagementState(state).renameModal.menuId,
    renameModalMenuName: (state: AppState) => getMenuManagementState(state).renameModal.menuName,
    renameModalInputValue: (state: AppState) => getMenuManagementState(state).renameModal.nameInputValue,
    renamePending: (state: AppState) => getMenuManagementState(state).renameModal.pending,
    renameError: (state: AppState) => getMenuManagementState(state).renameModal.error,

    deleteModalOpen: (state: AppState) => getMenuManagementState(state).deleteModal.open,
    deleteModalMenuId: (state: AppState) => getMenuManagementState(state).deleteModal.menuId,
    deleteModalMenuName: (state: AppState) => getMenuManagementState(state).deleteModal.menuName,
    deletePending: (state: AppState) => getMenuManagementState(state).deleteModal.pending,
    deleteError: (state: AppState) => getMenuManagementState(state).deleteModal.error,

    copyModalOpen: (state: AppState) => getMenuManagementState(state).copyModal.open,
    copyModalMenuId: (state: AppState) => getMenuManagementState(state).copyModal.menuId,
    copyModalMenuName: (state: AppState) => getMenuManagementState(state).copyModal.menuName,
    copyPending: (state: AppState) => getMenuManagementState(state).copyModal.pending,
    copyError: (state: AppState) => getMenuManagementState(state).copyModal.error,
    copyToUserId: (state: AppState) => getMenuManagementState(state).copyModal.copyToUserId,
    copyToOtherCustomer: (state: AppState) => getMenuManagementState(state).copyModal.copyToOtherCustomer,

    copyNotification: (state: AppState) => getMenuManagementState(state).copyNotification,

    menus: (userId: string | null | undefined) => (state: AppState) => getData(state, userId)?.menus ?? [],
    menuName: (userId: string | null | undefined, menuId: string | null | undefined) => (state: AppState) => {
        if (userId == null || menuId == null) {
            return null;
        }
        const menus = getData(state, userId)?.menus;
        return menus?.find(m => m.id === menuId)?.name;
    },
    loading: (userId: string | null | undefined) => (state: AppState) => getData(state, userId)?.request?.loading ?? false
}

export {reducer as menuManagementReducer};
export {publicActions as menuManagementActions};
export {selectors as menuManagementSelectors};